// StepNameCategory - Name, Realms und Kategorien (Schritt 1)
// Multi-Realm Support: Ein Ort kann sowohl GoodLife als auch GoodWork sein

import React from 'react';
import {
  Palette,
  Sofa,
  Moon,
  Clock,
  Trees,
  LucideIcon,
  Briefcase,
  Heart,
  Check,
  // GoodWork Icons
  Users,
  Target,
  Calendar,
  Wrench,
  GraduationCap,
} from 'lucide-react';
import type { MainCategory, GoodWorkCategory, Realm, RealmInfo } from '../../../types';
import { MAIN_CATEGORIES, GOODWORK_CATEGORIES, REALMS } from '../../../types';

// Helper to get realm info
const getRealmInfo = (realm: Realm): RealmInfo => {
  return REALMS.find(r => r.id === realm) || REALMS[0];
};

// Icon mapping for all categories
const ICON_MAP: Record<string, LucideIcon> = {
  // GoodLife
  Palette,
  Sofa,
  Moon,
  Clock,
  Trees,
  // GoodWork
  Users,
  Target,
  Calendar,
  Wrench,
  GraduationCap,
};

interface FormData {
  name: string;
  realms: Realm[];
  category: MainCategory | '';
  goodworkCategory: GoodWorkCategory | '';
}

interface StepNameCategoryProps {
  formData: FormData;
  updateFormData: (updates: Partial<FormData>) => void;
}

export function StepNameCategory({ formData, updateFormData }: StepNameCategoryProps) {
  const hasGoodLife = formData.realms.includes('goodlife');
  const hasGoodWork = formData.realms.includes('goodwork');

  // Toggle realm selection (checkbox behavior)
  const toggleRealm = (realm: Realm) => {
    const currentRealms = [...formData.realms];
    const index = currentRealms.indexOf(realm);

    if (index >= 0) {
      // Remove realm (but ensure at least one remains)
      if (currentRealms.length > 1) {
        currentRealms.splice(index, 1);
        // Clear category if realm is removed
        if (realm === 'goodlife') {
          updateFormData({ realms: currentRealms, category: '' });
        } else {
          updateFormData({ realms: currentRealms, goodworkCategory: '' });
        }
      }
    } else {
      // Add realm
      currentRealms.push(realm);
      updateFormData({ realms: currentRealms });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Wie heißt der Ort?
        </h2>
        <p className="text-gray-600">
          Gib dem GoodPlace einen Namen und wähle die passenden Kategorien.
        </p>
      </div>

      {/* Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Name des Ortes <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => updateFormData({ name: e.target.value })}
          placeholder="z.B. Kaffeehaus Schmidt"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
        />
      </div>

      {/* Realm Selection - Multi-Select Checkboxes */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Art des Ortes <span className="text-red-500">*</span>
        </label>
        <p className="text-xs text-gray-500 mb-3">
          Ein Ort kann auch beides sein - z.B. ein Café das auch zum Arbeiten geeignet ist.
        </p>
        <div className="grid grid-cols-2 gap-3">
          {/* GoodLife Checkbox */}
          <button
            type="button"
            onClick={() => toggleRealm('goodlife')}
            className={`p-4 rounded-xl border-2 transition text-left relative ${
              hasGoodLife
                ? 'border-emerald-600 bg-emerald-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            {hasGoodLife && (
              <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-emerald-600 flex items-center justify-center">
                <Check className="w-3 h-3 text-white" />
              </div>
            )}
            <div className="flex items-center gap-3 mb-2">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                hasGoodLife
                  ? 'bg-emerald-600 text-white'
                  : 'bg-gray-100 text-gray-600'
              }`}>
                <Heart className="w-5 h-5" />
              </div>
              <span className={`font-medium ${
                hasGoodLife ? 'text-emerald-700' : 'text-gray-900'
              }`}>
                {getRealmInfo('goodlife').label}
              </span>
            </div>
            <p className="text-xs text-gray-500 leading-relaxed">
              {getRealmInfo('goodlife').description}
            </p>
          </button>

          {/* GoodWork Checkbox */}
          <button
            type="button"
            onClick={() => toggleRealm('goodwork')}
            className={`p-4 rounded-xl border-2 transition text-left relative ${
              hasGoodWork
                ? 'border-indigo-600 bg-indigo-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            {hasGoodWork && (
              <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-indigo-600 flex items-center justify-center">
                <Check className="w-3 h-3 text-white" />
              </div>
            )}
            <div className="flex items-center gap-3 mb-2">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                hasGoodWork
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-600'
              }`}>
                <Briefcase className="w-5 h-5" />
              </div>
              <span className={`font-medium ${
                hasGoodWork ? 'text-indigo-700' : 'text-gray-900'
              }`}>
                {getRealmInfo('goodwork').label}
              </span>
            </div>
            <p className="text-xs text-gray-500 leading-relaxed">
              {getRealmInfo('goodwork').description}
            </p>
          </button>
        </div>
      </div>

      {/* GoodLife Category - nur anzeigen wenn GoodLife gewählt */}
      {hasGoodLife && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <span className="inline-flex items-center gap-2">
              <Heart className="w-4 h-4 text-emerald-600" />
              GoodLife-Kategorie <span className="text-red-500">*</span>
            </span>
          </label>
          <p className="text-xs text-gray-500 mb-3">
            Welche Art von Freizeitort ist das?
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {MAIN_CATEGORIES.map((cat) => {
              const IconComponent = ICON_MAP[cat.icon];
              const isSelected = formData.category === cat.id;

              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => updateFormData({ category: cat.id })}
                  className={`p-4 rounded-xl border-2 transition text-left ${
                    isSelected
                      ? 'border-emerald-600 bg-emerald-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    {IconComponent && (
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        isSelected
                          ? 'bg-emerald-600 text-white'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        <IconComponent className="w-5 h-5" />
                      </div>
                    )}
                    <span className="font-medium text-gray-900">{cat.label}</span>
                  </div>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    {cat.description}
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* GoodWork Category - nur anzeigen wenn GoodWork gewählt */}
      {hasGoodWork && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <span className="inline-flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-indigo-600" />
              GoodWork-Kategorie <span className="text-red-500">*</span>
            </span>
          </label>
          <p className="text-xs text-gray-500 mb-3">
            Welche Art von Arbeitsort ist das?
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {GOODWORK_CATEGORIES.map((cat) => {
              const IconComponent = ICON_MAP[cat.icon];
              const isSelected = formData.goodworkCategory === cat.id;

              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => updateFormData({ goodworkCategory: cat.id })}
                  className={`p-4 rounded-xl border-2 transition text-left ${
                    isSelected
                      ? 'border-indigo-600 bg-indigo-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    {IconComponent && (
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        isSelected
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        <IconComponent className="w-5 h-5" />
                      </div>
                    )}
                    <span className="font-medium text-gray-900">{cat.label}</span>
                  </div>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    {cat.description}
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default StepNameCategory;
